import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAuth } from '~/auth';

import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import { Button, Paper, alpha } from '@mui/material';

import type { ThreadContextSnapshot } from './AiChatContext';
import { useAdminChatContextSnapshot } from './useAdminChatContextSnapshot';

const PANEL_DEFAULT_OFFSET = 16;
const PANEL_VIEWPORT_GUTTER = PANEL_DEFAULT_OFFSET * 2;
const DEFAULT_PANEL_WIDTH = 440;
const DEFAULT_PANEL_HEIGHT = 760;

type AiOpsChatEmbedMessage =
  | { type: 'ai-ops-chat:auth-token'; token: string }
  | { type: 'ai-ops-chat:context'; context: ThreadContextSnapshot };

function buildEmbedUrl(origin: string, contextSnapshot: ThreadContextSnapshot | null): string {
  const url = new URL('/embed/chat', origin);

  if (contextSnapshot?.customerId) {
    url.searchParams.set('customer_id', contextSnapshot.customerId);
  }
  if (contextSnapshot?.customerName) {
    url.searchParams.set('customer_name', contextSnapshot.customerName);
  }
  if (contextSnapshot?.orderId) {
    url.searchParams.set('order_id', contextSnapshot.orderId);
  }
  if (contextSnapshot?.department) {
    url.searchParams.set('department', contextSnapshot.department);
  }
  if (contextSnapshot?.mode) {
    url.searchParams.set('mode', contextSnapshot.mode);
  }
  if (contextSnapshot?.page) {
    url.searchParams.set('page', contextSnapshot.page);
  }

  return url.toString();
}

export default function AiOpsChatIframeDrawer() {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const { getAccessToken } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [iframeSrc, setIframeSrc] = useState<string | null>(null);
  const contextSnapshot = useAdminChatContextSnapshot();
  const aiOpsChatOrigin = window.configs?.AI_OPS_CHAT_ORIGIN || null;
  const currentEmbedUrl = useMemo(
    () => (aiOpsChatOrigin ? buildEmbedUrl(aiOpsChatOrigin, contextSnapshot) : null),
    [aiOpsChatOrigin, contextSnapshot],
  );

  const postToIframe = useCallback(
    (message: AiOpsChatEmbedMessage) => {
      if (!aiOpsChatOrigin) {
        return;
      }

      iframeRef.current?.contentWindow?.postMessage(message, aiOpsChatOrigin);
    },
    [aiOpsChatOrigin],
  );

  const postAuthToken = useCallback(async () => {
    const token = await getAccessToken().catch(() => null);

    if (token) {
      postToIframe({ type: 'ai-ops-chat:auth-token', token });
    }
  }, [getAccessToken, postToIframe]);

  const postContext = useCallback(() => {
    if (contextSnapshot) {
      postToIframe({ type: 'ai-ops-chat:context', context: contextSnapshot });
    }
  }, [contextSnapshot, postToIframe]);

  const postAuthTokenAndContext = useCallback(() => {
    void postAuthToken();
    postContext();
  }, [postAuthToken, postContext]);

  const openChat = useCallback(() => {
    setIframeSrc(currentEmbedUrl);
    setIsOpen(true);
  }, [currentEmbedUrl]);

  useEffect(() => {
    if (isOpen) {
      void postAuthToken();
    }
  }, [isOpen, postAuthToken]);

  useEffect(() => {
    if (isOpen) {
      postContext();
    }
  }, [isOpen, postContext]);

  useEffect(() => {
    if (!isOpen || !aiOpsChatOrigin) {
      return undefined;
    }

    function handleMessage(event: MessageEvent) {
      if (event.origin !== aiOpsChatOrigin || event.source !== iframeRef.current?.contentWindow) {
        return;
      }

      if (event.data?.type === 'ai-ops-chat:close') {
        setIsOpen(false);
        return;
      }

      if (event.data?.type === 'ai-ops-chat:ready') {
        postAuthTokenAndContext();
      }

      if (event.data?.type === 'ai-ops-chat:auth-required') {
        void postAuthToken();
      }
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [aiOpsChatOrigin, isOpen, postAuthToken, postAuthTokenAndContext]);

  if (!currentEmbedUrl) {
    return null;
  }

  return (
    <>
      {!isOpen ? (
        <Button
          variant="contained"
          size="small"
          onClick={openChat}
          aria-label="Open AI chat"
          startIcon={<SmartToyOutlinedIcon sx={{ fontSize: 16 }} />}
          sx={{
            position: 'fixed',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 99999,
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: 0.4,
            py: 1.25,
            px: 0.75,
            minWidth: 0,
            minHeight: 110,
            borderRadius: '12px 0 0 12px',
            boxShadow: (theme) =>
              `0 8px 24px -8px ${alpha(theme.palette.primary.main, 0.55)}, 0 2px 6px -2px ${alpha(
                theme.palette.primary.main,
                0.3,
              )}`,
            background: (theme) =>
              `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            writingMode: 'vertical-rl',
            textOrientation: 'mixed',
            gap: 0.75,
            '& .MuiButton-startIcon': {
              margin: 0,
            },
          }}
        >
          AI Chat
        </Button>
      ) : null}

      {isOpen ? (
        <Paper
          elevation={0}
          role="dialog"
          aria-label="AI chat panel"
          sx={{
            position: 'fixed',
            right: PANEL_DEFAULT_OFFSET,
            bottom: PANEL_DEFAULT_OFFSET,
            width: `min(${DEFAULT_PANEL_WIDTH}px, calc(100vw - ${PANEL_VIEWPORT_GUTTER}px))`,
            height: `min(${DEFAULT_PANEL_HEIGHT}px, calc(100dvh - ${PANEL_VIEWPORT_GUTTER}px))`,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: 3,
            border: 1,
            borderColor: (theme) => alpha(theme.palette.divider, 0.6),
            bgcolor: 'background.paper',
            boxShadow: '0 24px 60px -16px rgba(15, 23, 42, 0.22), 0 8px 20px -10px rgba(15, 23, 42, 0.1)',
            zIndex: 1300,
          }}
        >
          <iframe
            ref={iframeRef}
            title="AI Ops chatbot"
            src={iframeSrc || currentEmbedUrl}
            onLoad={postAuthTokenAndContext}
            style={{
              border: 0,
              flex: 1,
              minHeight: 0,
              width: '100%',
            }}
          />
        </Paper>
      ) : null}
    </>
  );
}
